/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SimulationFiles.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import {
  CustomButton,
  CustomDataGridTableB,
  CustomPagination,
} from "../../components";
import CloseIcon from "@mui/icons-material/Close";

import {
  HandleApiActions,
  labCar,
  legalRequirements,
  profileActions,
  userManagementApis,
} from "../../redux/actions";
import typeApprovalAPI from "../../redux/actions/typeApprovalAPI";
import { useDispatch, useSelector } from "react-redux";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import appTheme from "../../assets/AppTheme/appTheme";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import ViewDynammicFormDetails from "../admin-settings/DynamicForms/ViewDynammicFormDetails";
import { Grid } from "@mui/material";
import SortByOptionsTickets from "../../components/tickets/SortByOptionsTickets";
import FilterByOptionsTickets from "../../components/tickets/FilterByOptionsTickets";
import PropTypes from "prop-types";

const joinIfArray = (value) =>
  Array.isArray(value) ? value.join(",") : null;

const addFilterIfAny = (key, list) =>
  list?.length > 0 ? { [key]: joinIfArray(list) } : null;

const buildTicketRequestParams = ({
  selectedProjectDetails,
  selectedVariantDetails,
  selectOEM,
  selectTECH,
  selectTicketStatus,
  selectedZones,
  selectedEE,
  selectedVC,
  selectedSWM,
  selectedStandard,
  selectedChapter,
  selectedLR,
  page,
  rowsPerPage,
  selectColumn,
  selectOrder,
  selectedTicketsType,
}) => ({
  project_id: selectedProjectDetails?.project_id,
  variant_id: selectedVariantDetails?.variant_id,
  ...addFilterIfAny("oem_users", selectOEM),
  ...addFilterIfAny("ts_users", selectTECH),
  ...addFilterIfAny("status", selectTicketStatus),
  ...addFilterIfAny("zones", selectedZones),
  ...addFilterIfAny("ee_id", selectedEE),
  ...addFilterIfAny("vc_id", selectedVC),
  ...addFilterIfAny("swm_id", selectedSWM),
  ...addFilterIfAny("ls_id", selectedStandard),
  ...addFilterIfAny("chapters", selectedChapter),
  ...addFilterIfAny("lr_id", selectedLR),
  page_number: page,
  page_size: rowsPerPage,
  ...(selectColumn ? { sort_column: selectColumn } : null),
  ...(selectOrder ? { sort_order: selectOrder } : null),
  tickets_type: selectedTicketsType,
});

const mapTicketRows = ({ ticketDetails, page, rowsPerPage, region }) => {
  const rows = ticketDetails
    ? [...ticketDetails]
      ?.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
      ?.map((item, index) => ({
        ...item,
        id: (page - 1) * rowsPerPage + index + 1,
        intr_id: item?.intr_id,
        tech_service_users: item?.tech_service_users,
        oem_users: item?.oem_users,
        users: item?.tech_service_users || item?.oem_users,
        ticket_id: item?.ticket_id,
        tickets_type: item?.tickets_type,
        status: item?.status,
        ticket_status: item?.ticket_id || item?.status,
        ls_details: item?.ls_details,
        lr_details: item?.lr_details,
        swm_details: item?.swm_details,
        vehicle_component_details: item?.vehicle_component_details,
        ee_details: item?.ee_details,
        description: item?.description,
        created_at: convertUtcToTimeZone(item?.created_at, region),
        created_by: item?.created_by,
        updated_at: convertUtcToTimeZone(item?.updated_at, region),
        updated_by: item?.updated_by,
        allData: item,
      }))
    : [];

  return {
    rows,
    hasMore: rows?.length >= rowsPerPage,
  };
};

const hasVehicleSystemDetails = (ticketDetails) =>
  ticketDetails?.some((item) => item?.vehicle_system_details?.name);

const hasVehicleComponentDetails = (ticketDetails) =>
  ticketDetails?.some((item) => item?.vehicle_component_details?.name);

const hasECUorSWMDetails = (ticketDetails) =>
  ticketDetails?.some(
    (item) => item?.swm_details?.swm_id || item?.ee_details?.ee_id,
  );

function SimulationFiles({ selectedProjectDetails, selectedVariantDetails }) {
  const dispatch = useDispatch();

  const profileDetails = useSelector((store) => store.profileReducer);
  const imagePathDetails = useSelector((state) => state?.profileReducer);
  const ticketStatusMetaApp = useSelector(
    (state) => state.settingsReducer?.metaData,
  );

  const EEDetails = useSelector((state) => state?.labCarReducer);
  const UserDetails = useSelector((state) => state.userManagementReducer);

  const LSDetails = useSelector((state) => state.legalRequirementsReducer);

  const [tableRowsData, setTableRowsData] = useState([]);
  const [ticketDetails, setTicketDetails] = useState([]);
  const [legalDialogData, setLegalDialogData] = useState(null);
  const [isLegalDialogOpen, setIsLegalDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(5);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [intervalId, setIntervalId] = useState(null);

  const [isEEDialogOpen, setIsEEDialogOpen] = useState(false);
  const [eeDialogData, setEEDialogData] = useState(null);
  const [dialogData, setDialogData] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [hasMore, setHasMore] = useState(false);

  const [selectedSortColumn] = useState("updated_at");
  const [selectedSortOrder] = useState("DESC");

  const [selectedSearchField] = useState("role_name");
  const [searchFieldValue] = useState("");

  const [openModal, setOpenModal] = React.useState(false);
  const [imagePath, setImagePath] = React.useState("");

  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [dynamicFormData, setDynamicFormData] = useState(null);

  const [selectColumn, setSelectColumn] = useState("updated_at");
  const [selectOrder, setSelectOrder] = useState("DESC");

  const [searchIntrId, setSearchIntrId] = useState("");
  const [searchTicketId, setSearchTicketId] = useState("");
  const ticketsType = { SWM: 1, EE: 2, VEHICLE: 3 };
  const [selectedTicketsType, setSelectedTicketsType] = useState(
    ticketsType?.EE,
  );

  const [selectTicketStatus, setSelectTicketStatus] = useState([]);

  const [selectedZones, setSelectedZones] = useState([]);
  const [selectOEM, setSelectOEM] = useState([]);
  const [selectTECH, setSelectTECH] = useState([]);
  const [selectedEE, setSelectedEE] = useState([]);
  const [selectSWMEE, setSelectSWMEE] = useState("");
  const [selectedSWM, setSelectedSWM] = useState([]);
  const [selectedVC, setSelectedVC] = useState([]);
  const [selectedStandard, setSelectedStandard] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState([]);
  const [selectedLR, setSelectedLR] = useState([]);

  const handleTemplateDialog = (confirm) => {
    setIsTemplateDialogOpen(false);
  };

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...userManagementApis?.get_users_list,
        params: {
          page_size: 2000,
        },
      }),
    );
  }, []);
  useEffect(() => {
    if (selectedProjectDetails && selectedVariantDetails) {
      dispatch(
        HandleApiActions({
          ...labCar?.get_ee_components,
          params: {
            project_id: selectedProjectDetails?.project_id,
            variant_id: selectedVariantDetails?.variant_id,
          },
          show_toast: false,
        }),
      );
    }
  }, [selectedProjectDetails, selectedVariantDetails]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...userManagementApis.get_roles_list,
        params: {
          page_size: 1000,
          role_type: 2,
        },
        show_toast: false,
      }),
    );
  }, []);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...labCar?.get_sw_modules,
        params: {
          project_id: selectedProjectDetails?.project_id,
          variant_id: selectedVariantDetails?.variant_id,
          ee_id: selectSWMEE,
        },
        show_toast: false,
      }),
    );
  }, [selectSWMEE]);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_standard_details,
        params: {
          page_number: 1,
          page_size: 200,
        },
      }),
    );
  }, []);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_requirements_details,
        params: {
          page_number: 1,
          page_size: 200,
        },
      }),
    );
  }, []);

  const reqObj = buildTicketRequestParams({
    selectedProjectDetails,
    selectedVariantDetails,
    selectOEM,
    selectTECH,
    selectTicketStatus,
    selectedZones,
    selectedEE,
    selectedVC,
    selectedSWM,
    selectedStandard,
    selectedChapter,
    selectedLR,
    page,
    rowsPerPage,
    selectColumn,
    selectOrder,
    selectedTicketsType,
  });

  const fetchTickets = async (requestObj) => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...typeApprovalAPI?.get_tickets,
          params: requestObj,
          show_toast: false,
        }),
      ),
    );

    if (resp.error) {
      setTicketDetails([]);
    } else {
      setTicketDetails(resp?.data);
    }
  };

  useEffect(() => {
    fetchTickets(reqObj);

    setTableRowsData([]);
  }, [
    selectedChapter,
    selectedStandard,
    selectedLR,
    page,
    rowsPerPage,
    selectOrder,
    selectOEM,
    selectedZones,
    selectTECH,
    selectedVC,
    selectColumn,
    selectedEE,
    selectTicketStatus,
    selectedTicketsType, // Ticket type filter
    selectedSearchField, // Selected search field
    searchFieldValue, // Search query value
    selectedProjectDetails?.project_id, // Project ID
    selectedVariantDetails?.variant_id, // Variant ID
  ]);
  useEffect(() => {
    const { rows, hasMore } = mapTicketRows({
      ticketDetails,
      page,
      rowsPerPage,
      region: profileDetails?.profile?.profile?.region,
    });

    setHasMore(hasMore);
    setTableRowsData(rows);
  }, [ticketDetails, selectedSortColumn, selectedSortOrder, page, rowsPerPage]);

  useEffect(() => {
    setImagePath(imagePathDetails?.profile_pic_path);
  }, [imagePathDetails]);

  useEffect(() => {
    if (
      !ticketStatusMetaApp ||
      !Array.isArray(ticketStatusMetaApp.snow_ticket_status)
    )
      return;
  }, [ticketStatusMetaApp]);

  const handleClearAllFilter = () => {
    setSearchIntrId("");
    setSearchTicketId("");
    setSelectOEM([]);
    setSelectTECH([]);
    setSelectedStandard([]);
    setSelectedChapter([]);
    setSelectedLR([]);
    setSelectedZones([]);
    setSelectedVC([]);
    setSelectedSWM([]);
    setSelectedEE([]);
    setSelectTicketStatus([]);
  };

  const handleImageChange = async (event, params) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const allowedExtensions = ["jpg", "jpeg", "png"];
      if (allowedExtensions.includes(fileExtension)) {
        const resp = await Promise.resolve(
          dispatch(
            HandleApiActions({
              ...profileActions.upload_profile_pic,
              show_toast: true,
              file: file,
              is_file: true,
            }),
          ),
        );

        await Promise.resolve(
          dispatch(
            HandleApiActions({
              api_name: "/ACM/intersections/upload/simulation-file",
              https_method: "PUT",
              params: {
                intr_id: params?.row?.intr_id,
                file_name: resp?.file_name,
              },
              show_toast: true,
            }),
          ),
        );

        await fetchTickets(reqObj);
      } else {
        toast.dark("Please select a valid image file", {
          type: "error",
          position: "bottom-right",
          autoClose: 3000,
        });
      }
    }
  };

  const handleImageRowClick = (row) => {
    setOpenModal(true);

    dispatch(
      HandleApiActions({
        ...profileActions.get_profile_pic,
        show_toast: true,
        file_name: row?.row?.simulation_file,
        is_file: true,
      }),
    );
  };

  const getTicketsTemplates = async ({ intrId }) => {
    const apiDetails = typeApprovalAPI.get_user_dynamic_forms;
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            project_id: selectedProjectDetails?.project_id,
            variant_id: selectedVariantDetails?.variant_id,
            intr_id: intrId,
            page_number: 1,
            page_size: 100,
            is_ticket_required: true,
          },
        }),
      ),
    );
    if (resp.error) {
      setDynamicFormData([]);
    } else {
      setDynamicFormData(resp?.data?.[0]);
    }
  };

  useEffect(() => {
    if (open) {
      const id = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(id);
            handleRedirect();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setIntervalId(id);

      return () => {
        clearInterval(id);
      };
    }
  }, [open]);

  const hasVehicleSystem = hasVehicleSystemDetails(ticketDetails);
  const hasVehicleComponent = hasVehicleComponentDetails(ticketDetails);
  const hasEcuOrSwm = hasECUorSWMDetails(ticketDetails);

  const COLUMNS = [
    {
      field: "intr_id",
      headerName: "Intersection Id",
      width: 120,
    },
    {
      field: "ticket_id",
      headerName: "TICKET ID",
      headerAlign: "center",
      width: 140,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center" }}>
          <LinkButton onClick={() => handleClickOpen(params.value)}>
            {params?.value}
          </LinkButton>

          <Dialog
            open={open}
            onClose={handleClose}
            BackdropProps={{
              style: { backgroundColor: "transparent" },
            }}
            PaperProps={{
              style: {
                width: "400px",
                height: "180px",
                borderRadius: "15px",
              },
            }}
          >
            <DialogTitle>
              <IconButton
                onClick={handleClose}
                sx={styles.closeButton}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", textAlign: "center" }}
              >
                Redirecting to ServiceNow <br />
                Ticketing tool!
              </Typography>

              <CountdownTimer
                onClick={handleRedirect}
                variant="h6"
                sx={{ mt: "20px", ml: "120px" }}
              >
                In ({timer})s
              </CountdownTimer>
            </DialogContent>
          </Dialog>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      headerAlign: "center",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Typography sx={{ textTransform: "uppercase" }}>
            {getTicketStatusName(params?.row?.status || "-")}
          </Typography>
        </Box>
      ),
    },
    {
      field: "changed_by",
      headerName: "Changed By",
      width: 270,
      renderCell: (params) => {

        return (
          <Box>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Name: </span>
              {params?.row?.updated_user_details[0]?.name || "-"}
            </Typography>
            <Typography variant="subtitle2">
              <span style={styles.txtBold}>Email: </span>
              {params?.row?.updated_user_details[0]?.email || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "simulation_file",
      headerName: "Simulation File",
      width: 170,
      renderCell: (params) => {

        return params?.row?.simulation_file === "" ? (
          <Box sx={{ mt: 1.4, ml: 2 }}>
            <Button
              size="small"
              sx={{ ...appTheme.commonBtnStyle }}
              variant="contained"
              component="label"
              disabled={params?.row?.status !== 101}
            >
              <span>Generate Test</span>
              <input
                type="file"
                hidden
                onChange={(event) => handleImageChange(event, params)}
              />
            </Button>
          </Box>
        ) : (
          <IconButton
            onClick={() => {
              handleImageRowClick(params);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        );
      },
    },
    ...(hasEcuOrSwm
      ? [
        {
          field: "ee_id",
          headerName: "MODULE TYPE",
          headerAlign: "left",
          width: 250,
          renderCell: (params) => {
            const { swm_details, ee_details } = params?.row ?? {};

            const renderDetails = (label, details) => (
              <>
                <Box sx={{ fontWeight: "bold", marginBottom: "8px" }}>
                  {label}
                </Box>
                {Object.entries(details).map(([key, value]) => (
                  <Box key={key}>
                    <strong>{key.replaceAll("_", " ").toUpperCase()}:</strong>
                    {value || "-"}
                  </Box>
                ))}
              </>
            );

            const renderCellBody = () => {
              if (swm_details && Object.keys(swm_details).length > 0) {
                return (
                  <>
                    {renderDetails("SOFTWARE MODULE", {
                      Name: swm_details?.name,
                      Version: swm_details?.version,
                      Description: swm_details?.description,
                    })}
                    {swm_details?.name && (
                      <Box sx={{ textAlign: "left", marginTop: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() =>
                            handleSWMViewButtonClick(params?.row?.allData)
                          }
                          sx={{
                            ...appTheme.commonBtnStyle,
                            fontSize: "12px",
                          }}
                          size="small"
                        >
                          View more {" >>"}
                        </Button>
                      </Box>
                    )}
                  </>
                );
              }

              if (ee_details && Object.keys(ee_details).length > 0) {
                return (
                  <>
                    {renderDetails("EE COMPONENT", {
                      "Supplier Name": ee_details?.supplier_name,
                      "Assembly Number": ee_details?.assembly_number,
                      Version: ee_details?.version,
                      Description: ee_details?.description,
                    })}
                    {params?.row?.allData?.dependencies?.ee_components && (
                      <Box sx={{ textAlign: "left", marginTop: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() =>
                            handleEEViewButtonClick(params?.row?.allData)
                          }
                          sx={{
                            ...appTheme.commonBtnStyle,
                            fontSize: "12px",
                          }}
                          size="small"
                        >
                          View more {" >>"}
                        </Button>
                      </Box>
                    )}
                  </>
                );
              }

              return <Box sx={{ color: "gray" }}>No Data Available</Box>;
            };

            return (
              <Box sx={{ textAlign: "left", fontSize: "14px" }}>
                {renderCellBody()}
              </Box>
            );
          },
        },
      ]
      : []),

    ...(hasVehicleSystem
      ? [
        {
          field: "vehicle_system_details",
          headerName: "VEHICLE SYSTEM",
          headerAlign: "left",
          width: 250,
          renderCell: (params) => {
            const { vehicle_system_details } = params?.row ?? {};
            return (
              <Box sx={{ textAlign: "left", fontSize: "14px" }}>
                <Box>
                  <strong>Assembly Number:</strong>{" "}
                  {vehicle_system_details?.assembly_number || "-"}
                </Box>
                <Box>
                  <strong>Name:</strong> {vehicle_system_details?.name || "-"}
                </Box>
                <Box>
                  <strong>Description:</strong>{" "}
                  {vehicle_system_details?.description || "-"}
                </Box>
              </Box>
            );
          },
        },
      ]
      : []),
    ...(hasVehicleComponent
      ? [
        {
          field: "vehicle_component_details",
          headerName: "VEHICLE COMPONENT",
          headerAlign: "left",
          width: 250,
          renderCell: (params) => {
            const { vehicle_component_details } = params?.row ?? {};
            return (
              <Box sx={{ textAlign: "left", fontSize: "14px" }}>
                <Box>
                  <strong>Name:</strong>{" "}
                  {vehicle_component_details?.name || "-"}
                </Box>
                <Box>
                  <strong>Component:</strong>{" "}
                  {vehicle_component_details?.component_number || "-"}
                </Box>
                <Box>
                  <strong>Description:</strong>{" "}
                  {vehicle_component_details?.description || "-"}
                </Box>
              </Box>
            );
          },
        },
      ]
      : []),

    {
      field: "ls_lr_details",
      headerName: "LEGAL DETAILS",
      headerAlign: "left",
      width: 250,

      renderCell: (params) => {
        const { lr_details, ls_details } = params?.row ?? {};
        return (
          <Box sx={{ textAlign: "left", fontSize: "14px" }}>
            <Box>
              <strong>Chapter:</strong> {lr_details?.chapter || "-"}
            </Box>
            <Box>
              <strong>Requirement Number:</strong>{" "}
              {lr_details?.req_number || "-"}
            </Box>
            <Box>
              <strong>Standard Number:</strong>{" "}
              {ls_details?.standard_number || "-"}
            </Box>
            <Box>
              <strong>Revision:</strong> {ls_details?.revision || "-"}
            </Box>

            <Box sx={{ textAlign: "left", marginTop: 2 }}>
              <Button
                variant="contained"
                onClick={() => handleLEGALViewButtonClick(params?.row?.allData)}
                sx={{ ...appTheme.commonBtnStyle, fontSize: "12px" }}
                size="small"
              >
                View more{" >>"}
              </Button>
            </Box>
          </Box>
        );
      },
    },

    {
      field: "attachments",
      headerName: "ATTACHMENTS",
      headerAlign: "center",
      width: 240,
      renderCell: (params) => (
        <Box sx={{ mt: 1.4, ml: 2, textAlign: "center" }}>
          <CustomButton
            size="small"
            variant="contained"
            btnName="View"
            handleOnClick={() => {
              getTicketsTemplates({ intrId: params?.row?.intr_id });
              setIsTemplateDialogOpen(true);
            }}
          />
        </Box>
      ),
    },
  ];

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handlePageSizeChange = (event) => {
    setRowsPerPage(event.target.value);
    setPage(1); // Reset to the first page when page size changes
  };

  const getTicketStatusName = (statusCode) => {
    return ticketStatus[statusCode] || "-";
  };

  const toggleEEDialog = (newOpen) => () => {
    setIsEEDialogOpen(newOpen);
  };

  const toggleDialog = (newOpen) => () => {
    setIsDialogOpen(newOpen);
  };

  const toggleLegalDialog = (newOpen) => () => {
    setIsLegalDialogOpen(newOpen);
  };

  const handleSWMViewButtonClick = (item) => {
    const modules = item?.dependencies?.software_modules || [];
    const vehicleFunctions = item?.swm_details?.vehicle_functions;

    const items2 = modules.map((module) => ({
      swm_id: module?.swm_id,
      name: module?.name,
      version: module?.version,
      description: module?.description,
      ee_details: module?.ee_details || [], // Include ee_details in each module
    }));

    const eeDetails = modules.flatMap((module) => module?.ee_details) || [];

    setDialogData({
      heading1: `Vehicle Functions`,
      items1:
        vehicleFunctions?.map((vehicleFunction) => ({
          name: vehicleFunction?.name,
          abbreviation: vehicleFunction?.abbreviation,
        })) || [],
      heading2: `Dependent Software Modules`,
      items2: items2, // Use the combined items
      heading3: `EE Details`,
      items3: eeDetails.map((ee) => ({
        ee_id: ee?.ee_id,
        assembly_number: ee?.assembly_number,
        description: ee?.description,
        supplier_name: ee?.supplier_name,
        version: ee?.version,
      })),
    });

    toggleDialog(true)();
  };

  const handleEEViewButtonClick = (item) => {
    const modules = item?.dependencies?.ee_components || [];
    const vehicleSystems = item?.ee_details?.vehicle_systems;

    setEEDialogData({
      heading1: `Vehicle Systems`,
      items1:
        vehicleSystems?.map((vs) => ({
          name: vs?.name,
          abbreviation: vs?.abbreviation,
        })) || [],
      heading2: `Dependent EE Components`,
      items2: modules.map((ee) => ({
        ee_id: ee?.ee_id,
        assembly_number: ee?.assembly_number,
        description: ee?.description,
        supplier_name: ee?.supplier_name,
        version: ee?.version,
      })),
    });

    toggleEEDialog(true)();
  };

  const handleLEGALViewButtonClick = (item) => {
    const lr_details = item?.lr_details || {};
    const ls_details = item?.ls_details || {};



    const items2 = [
      {
        lr_id: lr_details.lr_id,
        chapter: lr_details.chapter,
        description: lr_details.description,
        req_number: lr_details.req_number,
      },
    ];

    const items3 = [
      {
        ls_id: ls_details.ls_id,
        standard_number: ls_details.standard_number,
        name: ls_details.name,
        revision: ls_details.revision,
        description: ls_details.description,
        chapters: ls_details.chapters,
      },
    ];

    setLegalDialogData({
      heading2: `Legal Requirement`,
      items2: items2,
      items3: items3, // Optionally include ls_details if needed
    });

    toggleLegalDialog(true)();
  };


  const getUpdatedState = (prev, key, verifyChecked) => {
    return verifyChecked ? [...prev, key] : prev.filter((item) => item !== key);
  };

  const updateState = (setterFunction, key, verifyChecked, title) => {
    setterFunction((prev) => {
      return getUpdatedState(prev, key, verifyChecked);
    });
  };

  const handleCheckboxChange = (e, key, title) => {
    const verifyChecked = e.target.checked;

    if (title === "Ticket Status") {
      updateState(setSelectTicketStatus, key, verifyChecked, title);
    } else if (title === "Zones") {
      updateState(setSelectedZones, key, verifyChecked, title);
    } else if (title === "EE Components") {
      updateState(setSelectedEE, key, verifyChecked, title);
    } else if (title === "Vehicle Components") {
      updateState(setSelectedVC, key, verifyChecked, title);
    } else if (title === "Standards") {
      updateState(setSelectedStandard, key, verifyChecked, title);
    } else if (title === "Chapters") {
      updateState(setSelectedChapter, key, verifyChecked, title);
    } else if (title === "E/E Components ") {
      if (selectSWMEE === key) {
        setSelectSWMEE("");
      } else {
        setSelectSWMEE(key);
      }
    } else if (title === "SWM Components") {
      updateState(setSelectedSWM, key, verifyChecked, title);
    } else if (title === "Legal Requirements") {
      updateState(setSelectedLR, key, verifyChecked, title);
    } else if (title === "TA requestors") {
      updateState(setSelectOEM, key, verifyChecked, title);
    } else if (title === "TA reviewer") {
      updateState(setSelectTECH, key, verifyChecked, title);
    }
  };

  const handleClickOpen = (ticket_id) => {
    setCurrentTicketId(ticket_id);
    setOpen(true);
    setTimer(5);
  };

  const handleClose = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
    }
  };

  const handleRedirect = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (currentTicketId) {
      const linkType = currentTicketId.startsWith("INC")
        ? "incident.do"
        : "sn_customerservice_case.do";
      window.open(
        `https://ven07074.service-now.com/${linkType}?sysparm_query=number=${currentTicketId}`,
        "_blank",
      );
    }
  };

  return (
    <Box sx={styles.mainContainer}>
      <SortByOptionsTickets
        selectColumn={selectColumn}
        selectOrder={selectOrder}
        setSelectColumn={setSelectColumn}
        setSelectOrder={setSelectOrder}
      />
      <Grid container spacing={2}>
        <Grid item md={3}>
          <Box
            sx={{
              minHeight: "500px",
              height: "500px",
              overflowY: "auto",
              padding: "5px",

              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
          >
            <FilterByOptionsTickets
              selectedProjectDetails={selectedProjectDetails}
              setSearchTicketId={setSearchTicketId}
              setSearchIntrId={setSearchIntrId}
              searchTicketId={searchTicketId}
              searchIntrId={searchIntrId}
              selectOEM={selectOEM}
              selectTECH={selectTECH}
              UserDetails={UserDetails}
              ticketsType={ticketsType}
              appMetaDetails={ticketStatusMetaApp}
              selectedVariantDetails={selectedVariantDetails}
              EEDetails={EEDetails}
              LSDetails={LSDetails}
              selectedTicketsType={selectedTicketsType}
              setSelectedTicketsType={setSelectedTicketsType}
              selectTicketStatus={selectTicketStatus}
              handleCheckboxChange={handleCheckboxChange}
              selectedZones={selectedZones}
              selectedEE={selectedEE}
              selectedVC={selectedVC}
              selectedStandard={selectedStandard}
              selectedChapter={selectedChapter}
              selectedLR={selectedLR}
              selectSWMEE={selectSWMEE}
              selectedSWM={selectedSWM}
              handleClearAllFilter={handleClearAllFilter}
              fetchTickets={fetchTickets}
              reqObj={reqObj}
            />
          </Box>
        </Grid>
        <Grid item md={9}>
          <Box>
            <Paper elevation={6} sx={styles.paper}>
              <CustomDataGridTableB
                tableRowsData={tableRowsData}
                columns={COLUMNS}
                rowCount={tableRowsData.length}
                isMultiLine={true}
                handleTableRowClick={() => null}
              />
            </Paper>
            <Paper elevation={4} sx={styles.paginationPaper}>
              <CustomPagination
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                hasMore={hasMore}
              />
            </Paper>
          </Box>
        </Grid>
      </Grid>

      <Dialog
        open={isEEDialogOpen}
        keepMounted
        onClose={toggleEEDialog(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: "350px",
            boxSizing: "border-box",
            padding: 0,
          },
        }}
      >
        <Box sx={styles.drawerContainer}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            EE Components
          </Typography>
          <Divider sx={styles.divider} />

          <IconButton onClick={toggleEEDialog(false)} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>

          <Box sx={styles.drawerWrap}>
            <Paper sx={styles.paperDrawer}>
              <Typography
                variant="h6"
                sx={{
                  marginBottom: 1,
                  fontWeight: "bold",
                  textAlign: "left",
                }}
              >
                System Functions
              </Typography>
              <Divider sx={styles.divider} />

              {eeDialogData?.items1?.map((vehicleSystem, index) => (
                <Paper
                  key={`${index}-${vehicleSystem?.abbreviation}`}
                  sx={styles.cardDrawer}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: "15px",
                      display: "flex",
                    }}
                  >
                    <strong>{vehicleSystem?.abbreviation}</strong>:{" "}
                    {vehicleSystem?.name}
                  </Typography>
                </Paper>
              ))}

              {eeDialogData?.items1?.length <= 0 && (
                <Typography sx={{ textAlign: "center" }}>
                  No data found.
                </Typography>
              )}
            </Paper>
          </Box>

          <Box sx={styles.drawerWrap}>
            {eeDialogData?.heading2 && (
              <Paper sx={styles.paperDrawer}>
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: 1,
                    fontWeight: "bold",
                    textAlign: "left",
                  }}
                >
                  Dependent EE Components
                </Typography>
                <Divider sx={styles.divider} />
                <Box>
                  {eeDialogData?.items2?.map((module, index) => (
                    <Paper
                      key={`${index}-${module?.assembly_number}`}
                      sx={styles.cardDrawer}
                    >
                      <Box
                        sx={{
                          border: "1px solid black",
                          padding: "10px",
                          borderRadius: "10px",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: "black",
                              fontSize: "15px",
                              textDecoration: "underline",
                            }}
                          >
                            EE Component
                          </Typography>
                          <Box sx={{ gap: "10px" }}>
                            {[
                              { header: "Name", value: module?.description },
                              { header: "Version", value: module?.version },
                              {
                                header: "Assembly Number",
                                value: module?.assembly_number,
                              },
                              {
                                header: "Supplier Name",
                                value: module?.supplier_name,
                              },
                            ].map((det, key) => (
                              <Typography
                                key={`${key}-${det?.value}-${det?.header}`}
                                sx={{ fontSize: "13px" }}
                              >
                                <strong>{det?.header}:</strong> {det?.value}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  ))}

                  {eeDialogData?.items2?.length <= 0 && (
                    <Typography sx={{ textAlign: "center" }}>
                      No data found.
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      </Dialog>
      <Dialog
        open={isDialogOpen}
        keepMounted
        onClose={toggleDialog(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: "350px",
            boxSizing: "border-box",
            padding: 0,
          },
        }}
      >
        <Box sx={styles.drawerContainer}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Software Module
          </Typography>
          <Divider sx={styles.divider} />
          <IconButton onClick={toggleDialog(false)} sx={styles.closeButton}>
            <CloseIcon />
          </IconButton>
          <Box sx={styles.drawerWrap}>
            <Paper sx={styles.paperDrawer}>
              <Typography
                variant="h6"
                sx={{
                  marginBottom: 1,
                  fontWeight: "bold",
                  textAlign: "left",
                }}
              >
                Vehicle Function
              </Typography>
              <Divider sx={styles.divider} />

              {dialogData?.items1?.map((vehicleFunction, index) => (
                <Paper
                  key={`${index}-${vehicleFunction.abbreviation}`}
                  sx={styles.cardDrawer}
                >
                  <Typography
                    sx={{
                      fontWeight: "bold",
                      fontSize: "15px",
                      display: "flex",
                    }}
                  >
                    <strong>{vehicleFunction.abbreviation}</strong>:{" "}
                    {vehicleFunction.name}
                  </Typography>
                </Paper>
              ))}

              {dialogData?.items1?.length <= 0 && (
                <Typography sx={{ textAlign: "center" }}>
                  No data found.
                </Typography>
              )}
            </Paper>
          </Box>
          <Box sx={styles.drawerWrap}>
            {dialogData?.heading2 && (
              <Paper sx={styles.paperDrawer}>
                <Typography
                  variant="h6"
                  sx={{
                    marginBottom: 1,
                    fontWeight: "bold",
                    textAlign: "left",
                  }}
                >
                  Dependent Software Modules
                </Typography>
                <Divider sx={styles.divider} />
                <Box>
                  {dialogData?.items2?.map((module, index) => (
                    <Paper
                      key={`${index}-${module?.swm_id}`}
                      sx={styles.cardDrawer}
                    >
                      <Box
                        sx={{
                          border: "1px solid black",
                          padding: "10px",
                          borderRadius: "10px",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: "black",
                              fontSize: "15px",
                              textDecoration: "underline",
                            }}
                          >
                            Software Module
                          </Typography>
                          <Box sx={{ gap: "10px" }}>
                            {[
                              { header: "ID", value: module?.swm_id },
                              { header: "Name", value: module?.name },
                              { header: "Version", value: module?.version },
                              {
                                header: "Description",
                                value: module?.description,
                              },
                            ].map((det, key) => (
                              <Typography
                                key={`${key}-${det?.header}`}
                                sx={{ fontSize: "13px" }}
                              >
                                <strong>{det?.header}:</strong> {det?.value}
                              </Typography>
                            ))}
                          </Box>
                        </Box>

                        {module?.ee_details && module.ee_details.length > 0 && (
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                fontSize: "15px",
                                paddingTop: "10px",
                                textDecoration: "underline",
                              }}
                            >
                              EE Component
                            </Typography>
                            {module.ee_details.map((ee, eeIndex) => (
                              <Box
                                key={`${eeIndex}-${ee?.assembly_number}`}
                                sx={{ gap: "10px" }}
                              >
                                {[
                                  { header: "Name", value: ee?.description },
                                  { header: "Version", value: ee?.version },
                                  {
                                    header: "Assembly Number",
                                    value: ee?.assembly_number,
                                  },
                                  {
                                    header: "Supplier Name",
                                    value: ee?.supplier_name,
                                  },
                                ].map((det, key) => (
                                  <Typography
                                    key={`${eeIndex}-${det?.header}-${det?.value}`}
                                    sx={{ fontSize: "13px" }}
                                  >
                                    <strong>{det?.header}:</strong> {det?.value}
                                  </Typography>
                                ))}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  ))}

                  {dialogData?.items2?.length <= 0 && (
                    <Typography sx={{ textAlign: "center" }}>
                      No data found.
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
          </Box>
          <Dialog
            open={isLegalDialogOpen}
            keepMounted
            onClose={toggleLegalDialog(false)}
            sx={{
              "& .MuiDrawer-paper": {
                width: "100%",
                maxWidth: "350px",
                boxSizing: "border-box",
                padding: 0,
              },
            }}
          >
            <Box sx={styles.drawerContainer}>
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                LEGAL DETAILS
              </Typography>
              <Divider sx={styles.divider} />

              <IconButton
                onClick={toggleLegalDialog(false)}
                sx={styles.closeButton}
              >
                <CloseIcon />
              </IconButton>

              <Box sx={styles.drawerWrap}>
                {legalDialogData?.heading2 && (
                  <Paper sx={styles.paperDrawer}>
                    <Box>
                      {legalDialogData?.items2.map((lr, index) => (
                        <Paper
                          key={`${index}-${lr?.lr_id}`}
                          sx={styles.cardDrawer}
                        >
                          <Box
                            sx={{
                              border: "1px solid black",
                              padding: "10px",
                              borderRadius: "10px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                color: "black",
                                fontSize: "15px",
                                textDecoration: "underline",
                              }}
                            >
                              Legal Requirements
                            </Typography>
                            <Box sx={{ gap: "10px" }}>
                              {[
                                { header: "ID", value: lr?.lr_id },
                                { header: "Chapter", value: lr?.chapter },
                                {
                                  header: "Requirement Number",
                                  value: lr?.req_number,
                                },
                                {
                                  header: "Description",
                                  value: lr?.description,
                                },
                              ].map((det, key) => (
                                <Typography
                                  key={`${key}-${det?.value}`}
                                  sx={{ fontSize: "13px" }}
                                >
                                  <strong>{det?.header}:</strong> {det?.value}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>

              <Dialog
                open={isLegalDialogOpen}
                keepMounted
                onClose={toggleLegalDialog(false)}
                sx={{
                  "& .MuiDrawer-paper": {
                    width: "100%",
                    maxWidth: "350px",
                    boxSizing: "border-box",
                    padding: 0,
                  },
                }}
              >
                <Box sx={styles.drawerContainer}>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    LEGAL DETAILS
                  </Typography>
                  <Divider sx={styles.divider} />

                  <IconButton
                    onClick={toggleLegalDialog(false)}
                    sx={styles.closeButton}
                  >
                    <CloseIcon />
                  </IconButton>

                  <Box sx={styles.drawerWrap}>
                    {legalDialogData?.heading2 && (
                      <Paper sx={styles.paperDrawer}>
                        <Box>
                          {legalDialogData?.items2.map((lr, index) => (
                            <Paper
                              key={`${index}-${lr?.lr_id}-${lr?.chapter}`}
                              sx={styles.cardDrawer}
                            >
                              <Box
                                sx={{
                                  border: "1px solid black",
                                  padding: "10px",
                                  borderRadius: "10px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    color: "black",
                                    fontSize: "15px",
                                    textDecoration: "underline",
                                  }}
                                >
                                  Legal Requirements
                                </Typography>
                                <Box sx={{ gap: "10px" }}>
                                  {[
                                    { header: "ID", value: lr?.lr_id },
                                    { header: "Chapter", value: lr?.chapter },
                                    {
                                      header: "Requirement Number",
                                      value: lr?.req_number,
                                    },
                                    {
                                      header: "Description",
                                      value: lr?.description,
                                    },
                                  ].map((det, key) => (
                                    <Typography
                                      key={`${det?.header}-${key}`}
                                      sx={{ fontSize: "13px" }}
                                    >
                                      <strong>{det?.header}:</strong>{" "}
                                      {det?.value}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </Paper>
                    )}
                  </Box>

                  <Box sx={styles.drawerWrap}>
                    {legalDialogData?.items3 && (
                      <Paper sx={styles.paperDrawer}>
                        <Box>
                          {legalDialogData?.items3.map((ls, lsindex) => (
                            <Paper
                              key={`${lsindex}-${ls?.standard_number}`}
                              sx={styles.cardDrawer}
                            >
                              <Box
                                sx={{
                                  border: "1px solid black",
                                  padding: "10px",
                                  borderRadius: "10px",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: "bold",
                                    color: "black",
                                    fontSize: "15px",
                                    textDecoration: "underline",
                                  }}
                                >
                                  Legal Standard
                                </Typography>
                                <Box sx={{ gap: "10px" }}>
                                  {[
                                    { header: "ID", value: ls?.ls_id },
                                    {
                                      header: "Standard Number",
                                      value: ls?.standard_number,
                                    },
                                    { header: "Name", value: ls?.name },
                                    { header: "Revision", value: ls?.revision },
                                    {
                                      header: "Description",
                                      value: ls?.description,
                                    },
                                    {
                                      header: "Chapters",
                                      value: ls?.chapters.join(", "),
                                    }, // Display chapters
                                  ].map((det, key) => (
                                    <Typography
                                      key={`${det?.header}-${key}`}
                                      sx={{ fontSize: "13px" }}
                                    >
                                      <strong>{det?.header}:</strong>{" "}
                                      {det?.value}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Dialog>
            </Box>
          </Dialog>
        </Box>
      </Dialog>
      {openModal && (
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">View Image</Typography>
              <IconButton
                onClick={() => {
                  setOpenModal(false);
                  setImagePath(null);
                }}
                color="inherit"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <img
              alt="Not found"
              src={imagePath}
              style={{
                height: "100%",
                width: "100%",
                objectFit: "contain",
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {dynamicFormData?.form_id ? (
        <ViewDynammicFormDetails
          data={dynamicFormData}
          open={isTemplateDialogOpen}
          handleClose={() => handleTemplateDialog(false)}
        />
      ) : (
        <Dialog
          fullWidth
          maxWidth={"sm"}
          open={isTemplateDialogOpen}
          onClose={() => handleTemplateDialog(false)}
        >
          <DialogTitle sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => handleTemplateDialog(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              style={{
                height: "370px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">No attachments found</Typography>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default SimulationFiles;

const styles = {
  filter: {
    position: "relative",
    top: "4px",
    right: "8px",
  },
  mainBox: {
    pt: 0,
    boxShadow: 3,
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
    height: "100vh",
    pr: 2,
    pl: 2,
  },
  box: {
    height: "100vh",
    width: "100%",
  },
  paper: {
    mt: "10px",
    boxShadow: 3,
    height: "500px",
    minHeight: "500px",
    width: "auto",
    scrollbarColor: "#888 #f1f1f1",
  },
  drawerContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "400px",
    padding: 2,
    position: "relative",
  },
  closeButton: {
    color: "red",
    position: "absolute",
    top: "4px",
    right: "8px",
  },
  divider: {
    border: "1px solid lightgrey",
    margin: "8px 0",
    marginY: 1,
  },
  paperDrawer: {
    marginBottom: 2,
    padding: 2,
    boxShadow: 10,
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
  },
  drawerWrap: {
    marginTop: 2,
  },

  dataTypographyKey: {
    fontWeight: "bold",
    fontSize: "14px",
    textAlign: "left",
    minWidth: "110px",
  },
  dataTypographyValue: {
    marginLeft: 1,
    fontSize: "14px",
    textAlign: "left",
  },
  dataBox: {
    display: "flex",
    flexDirection: "row",

    alignItems: "center",
    flexWrap: "wrap",
    gap: 1,
  },
  cardContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "100%",
  },
  card: {
    boxShadow: 3,
    width: "100%",
    height: "auto",
    marginBottom: 1,
    padding: 2,
  },
  cardContent: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dataTypography: {
    fontWeight: "bold",
    fontSize: "13px",
    textAlign: "left",
    minWidth: "120px",
  },
  dataTypographyCardKey: {
    fontWeight: "bold",
    fontSize: "15px",
    textAlign: "left",
    minWidth: "50px",
  },
  dataTypographyCardValue: {
    fontSize: "15px",
    textAlign: "left",
    minWidth: "50px",
  },
  btnsContainer: {
    display: "flex",
    justifyContent: "flex-end",
    mb: 1,
    gap: 1,
  },
  cardDrawer: { margin: 1, padding: 2, boxShadow: 3 },
  paginationPaper: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "5px",
  },
};

const ticketStatus = {
  100: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#000000",
        fontSize: "12px",
      }}
      label={"NEW"}
      size="medium"
    />
  ),
  101: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#0076a8",
        fontSize: "12px",
      }}
      label={"DRAFT"}
      size="medium"
    />
  ),
  150: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#0076a8",
        fontSize: "12px",
      }}
      label={"CONFIRM LEGAL REQUIREMENTS"}
      size="medium"
    />
  ),
  200: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#0076a8",
        fontSize: "12px",
      }}
      label={"ARTIFACT CONFIRMATION"}
      size="medium"
    />
  ),
  300: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#0076a8",
        fontSize: "12px",
      }}
      label={"EVIDENCE SUBMISSION"}
      size="medium"
    />
  ),
  400: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#0076a8",
        fontSize: "12px",
      }}
      label={"EVIDENCE REVIEW"}
      size="medium"
    />
  ),
  500: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#0076a8",
        fontSize: "12px",
      }}
      label={"TESTING AT TA reviewers"}
      size="medium"
    />
  ),
  600: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#26890D",
        fontSize: "12px",
      }}
      label={"RESOLVED"}
      size="medium"
    />
  ),
  999: (
    <Chip
      sx={{
        fontWeight: "bold",
        background: "none",
        color: "#26890D",
        fontSize: "12px",
      }}
      label={"CANCELLED"}
      size="medium"
    />
  ),
};

const LinkButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  color: theme.palette.primary.main,
  textDecoration: "underline",
  padding: 0,
  minWidth: "auto",
  "&:hover": {
    textDecoration: "underline",
    backgroundColor: "transparent",
  },
}));

const CountdownTimer = styled(Typography)(({ theme }) => ({
  backgroundColor: "green",
  color: "white",
  borderRadius: "30px",
  textAlign: "center",
  width: "100px",
  cursor: "pointer",
}));

SimulationFiles.propTypes = {
  selectedProjectDetails: PropTypes.object, // if it’s mandatory
  selectedVariantDetails: PropTypes.object, // if it’s mandatory
};
